<template>
  <div ref="phoneInputWrapper" class="phone-input-wrapper">
    <Select
      v-model:value="areaCode"
      class="area-code-select"
      popup-class-name="phone-select-popup"
      option-label-prop="value"
      :style="{ height }"
      :get-popup-container="getPopupContainer"
      :disabled="disabled"
      @change="handleAreaCodeChange"
    >
      <SelectOptGroup v-for="group in countryOptions" :key="group.label">
        <SelectOption
          v-for="option in group.options"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        >
          <div class="phone-select-item">
            <span class="area-code">{{ option.value }}</span>
            <span>{{ option.label }}</span>
          </div>
        </SelectOption>
      </SelectOptGroup>
    </Select>
    <div class="relative w-full">
      <Input
        v-model:value="phoneNumber"
        class="w-full phone-number-input"
        :placeholder="placeholder"
        :style="{ height }"
        :disabled="disabled"
        @change="handlePhoneNumberChange"
        @blur="doValidate"
        @keyup="$emit('keyup', $event)"
      />
      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CountryOption } from '@peerup/helper'

import { fetchCountryOptions, validatePhone } from '@peerup/helper'
import { Input, Select, SelectOptGroup, SelectOption } from 'ant-design-vue'
import { onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  value?: string // v-model:value支持
  areaCode?: string // 区号，外部传入时同步到内部
  height?: string
  placeholder?: string
  required?: boolean // 是否必填，默认 true
  disabled?: boolean // 是否禁用，默认 false
  i18nText?: {
    请输入手机号: string
    手机号不得小于11位: string
    手机号不得大于11位: string
    请输入正确的手机号: string
  }
}>(), {
  value: '',
  areaCode: '',
  height: '50px',
  placeholder: '请输入手机号',
  required: true,
  disabled: false,
  i18nText: () => ({
    请输入手机号: '请输入手机号',
    手机号不得小于11位: '手机号不得小于11位',
    手机号不得大于11位: '手机号不得大于11位',
    请输入正确的手机号: '请输入正确的手机号',
  }),
})

/**
 * PhoneSelect 组件 Emits 类型
 * @event change 区号或手机号变化时触发
 * @event validate 校验结果变化时触发
 * @event update:value v-model:value双向绑定事件
 * @event keyup 键盘按键释放事件
 */
const emit = defineEmits<{
  (e: 'change', value: { areaCode: string; phoneNumber: string }): void
  (e: 'validate', result: { valid: boolean; message: string; value: { areaCode: string; phoneNumber: string } }): void
  (e: 'update:value', value: string): void
  (e: 'keyup', event: KeyboardEvent): void
}>()

const phoneInputWrapper = ref<HTMLElement>()
const phoneNumber = ref('')
const areaCode = ref('+86')
const errorMessage = ref('')

// 监听value变化，实现双向绑定
watch(() => props.value, newValue => {
  if (newValue !== phoneNumber.value) {
    phoneNumber.value = newValue || ''
  }
}, { immediate: true })

// 监听外部传入的区号，同步到内部状态
watch(() => props.areaCode, newValue => {
  if (newValue && newValue !== areaCode.value) {
    areaCode.value = newValue
  }
}, { immediate: true })

const getPopupContainer = () => {
  return phoneInputWrapper.value?.closest('body')
}

// 校验手机号逻辑
async function doValidate() {
  let valid = true
  let message = ''
  if (!props.required && !phoneNumber.value) {
    // 非必填且为空，直接通过
    valid = true
    message = ''
  } else {
    try {
      await validatePhone({}, phoneNumber.value, areaCode.value, props.required, {
        请输入手机号: props.i18nText['请输入手机号'],
        手机号不得小于11位: props.i18nText['手机号不得小于11位'],
        手机号不得大于11位: props.i18nText['手机号不得大于11位'],
        请输入正确的手机号: props.i18nText['请输入正确的手机号'],
      })
    } catch (err) {
      valid = false
      message = err instanceof Error ? err.message : props.i18nText['请输入正确的手机号']
    }
  }
  errorMessage.value = message
  emit('validate', {
    valid,
    message,
    value: { areaCode: areaCode.value, phoneNumber: phoneNumber.value },
  })
}

// 触发双向绑定更新的辅助函数
const emitUpdate = () => {
  // 只支持 v-model:value
  emit('update:value', phoneNumber.value)
}

// 监听输入变化自动校验
watch([phoneNumber, areaCode], () => {
  doValidate()
  emitUpdate()
})

// 区号变更处理
const handleAreaCodeChange = () => {
  emit('change', { areaCode: areaCode.value, phoneNumber: phoneNumber.value })
  emitUpdate()
}

// 手机号变更处理
const handlePhoneNumberChange = () => {
  emit('change', { areaCode: areaCode.value, phoneNumber: phoneNumber.value })
  emitUpdate()
}

const countryOptions = ref<CountryOption[]>([])
onMounted(async () => {
  countryOptions.value = await fetchCountryOptions()
})

defineExpose({
  areaCode,
  phoneNumber,
})
</script>

<style lang="less">
.phone-select-popup {
  width: 220px !important;
  border-radius: 6px;

  .ant-select-item{
    padding: 8px;
  }

  .phone-select-item{
    display: flex;
    align-items: center;
    height: 100%;
    font-size: 12px;
    line-height: 12px;
    color: var(--neutral-500);

    .area-code{
      margin-right: 8px;
    }
  }
}
</style>

<style lang="less" scoped>
.phone-input-wrapper {
  position: relative;
  display: flex;
  gap: 10px;
  align-items: stretch;

  :deep(.ant-input){
    border-radius: 4px;
  }

  .area-code-select {
    flex-shrink: 0;
    width: 100px;

    :deep(.ant-select-selector){
      height:100%;
      border-radius: 4px;
    }

    :deep(.ant-select-selection-item) {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }

  .phone-number-input {
    flex: 1;
    padding: 0 23px;

    :deep(.ant-input) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  .error-message {
    position: absolute;
    top: 100%;
    left: 0;
    font-size: 14px;
    color: var(--functional-deep-red);
  }
}
</style>
